import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Star, StarOff, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BASE_SKILL_CATEGORIES = [
'Management',
'Strategy',
'Commercial Negotiations',
'Development',
'AI',
'Hardware Engineering',
'Legal',
'Accounting',
'Forecasting',
'Marketing',
'Sales',
'Product',
'Design',
'Data Analysis',
'Operations',
'Languages'];


const LANGUAGE_OPTIONS = [
'English',
'Spanish',
'French',
'German',
'Mandarin',
'Japanese',
'Korean',
'Arabic',
'Portuguese',
'Russian',
'Italian',
'Dutch',
'Swedish',
'Polish',
'Turkish',
'Hebrew',
'Hindi'];


export default function SkillsManager({ skills = [], onChange, readonly = false }) {
  const queryClient = useQueryClient();
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillLevel, setNewSkillLevel] = useState(1);
  const [useCustomSkill, setUseCustomSkill] = useState(false);
  const [showLanguageDialog, setShowLanguageDialog] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showCustomCategoryInput, setShowCustomCategoryInput] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const isAdmin = user?.role === 'admin';

  const { data: customCategories = [] } = useQuery({
    queryKey: ['skillCategories'],
    queryFn: () => base44.entities.SkillCategory.list(),
    initialData: []
  });

  const createCategoryMutation = useMutation({
    mutationFn: (categoryName) => base44.entities.SkillCategory.create({
      category_name: categoryName,
      proposed_by: user?.email,
      status: 'pending'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillCategories'] });
      setCustomCategoryName('');
      setShowCustomCategoryInput(false);
    }
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id) => base44.entities.SkillCategory.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillCategories'] });
    }
  });

  const allCategories = [
  ...BASE_SKILL_CATEGORIES,
  ...customCategories.map((c) => c.category_name)];


  const handleAddSkill = () => {
    if (!newSkillName.trim()) return;

    const newSkill = {
      name: newSkillName.trim(),
      level: newSkillLevel,
      xp: 0
    };

    onChange([...skills, newSkill]);
    setNewSkillName('');
    setNewSkillLevel(1);
    setUseCustomSkill(false);
  };

  const handleAddLanguages = () => {
    const newSkills = selectedLanguages.map((lang) => ({
      name: lang,
      level: newSkillLevel,
      xp: 0
    }));

    onChange([...skills, ...newSkills]);
    setSelectedLanguages([]);
    setShowLanguageDialog(false);
    setNewSkillName('');
    setNewSkillLevel(1);
  };

  const handleCategorySelect = (category) => {
    if (category === 'Languages') {
      setShowLanguageDialog(true);
    } else if (category === '+Add New') {
      setShowCustomCategoryInput(true);
    } else {
      setNewSkillName(category);
    }
  };

  const handleAddCustomCategory = () => {
    if (!customCategoryName.trim()) return;
    createCategoryMutation.mutate(customCategoryName.trim());
    setNewSkillName(customCategoryName.trim());
  };

  const handleRemoveSkill = (index) => {
    onChange(skills.filter((_, i) => i !== index));
  };

  const handleUpdateLevel = (index, newLevel) => {
    const updatedSkills = [...skills];
    updatedSkills[index] = { ...updatedSkills[index], level: newLevel };
    onChange(updatedSkills);
  };

  const renderStars = (level) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) =>
        star <= level ?
        <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" /> :

        <StarOff key={star} className="w-4 h-4 text-slate-600" />

        )}
      </div>);

  };

  const getLevelLabel = (level) => {
    const labels = {
      1: 'Novice',
      2: 'Beginner',
      3: 'Intermediate',
      4: 'Advanced',
      5: 'Expert'
    };
    return labels[level] || 'Unknown';
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {skills.map((skill, index) =>
        <div key={index} className="flex items-center justify-between p-3 bg-slate-950 rounded-lg border border-slate-800">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-300">{skill.name}</span>
                <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                  {getLevelLabel(skill.level)}
                </Badge>
              </div>
              <div className="flex items-center gap-3 mt-2">
                {renderStars(skill.level)}
                {!readonly &&
              <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((level) =>
                <button
                  key={level}
                  onClick={() => handleUpdateLevel(index, level)}
                  className={`px-2 py-0.5 text-xs rounded ${
                  skill.level === level ?
                  'bg-indigo-600 text-white' :
                  'bg-slate-800 text-slate-500 hover:bg-slate-700'}`
                  }>

                        {level}
                      </button>
                )}
                  </div>
              }
              </div>
            </div>
            {!readonly &&
          <Button
            size="icon"
            variant="ghost"
            onClick={() => handleRemoveSkill(index)}
            className="h-8 w-8 text-slate-500 hover:text-red-400">

                <X className="w-4 h-4" />
              </Button>
          }
          </div>
        )}

        {skills.length === 0 &&
        <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg">
            <p className="text-sm text-slate-500">No skills added yet</p>
          </div>
        }
      </div>

      {!readonly &&
      <div className="p-4 bg-slate-100 rounded-lg border border-slate-300 space-y-3">
          <h4 className="text-sm font-semibold text-slate-700">Add New Skill</h4>
          
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-2">
              <Label className="text-xs text-slate-700">Skill</Label>
              {useCustomSkill ?
            <Input data-testid="officers-text-xs-text-slate-700-input"
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              placeholder="Enter custom skill..."
              className="bg-white border-slate-300 text-slate-900" /> :


            <Select value={newSkillName} onValueChange={handleCategorySelect}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                    <SelectValue placeholder="Select skill..." />
                  </SelectTrigger>
                  <SelectContent>
                    {BASE_SKILL_CATEGORIES.map((skill) =>
                <SelectItem key={skill} value={skill}>
                        {skill}
                      </SelectItem>
                )}
                    {customCategories.map((cat) =>
                <SelectItem key={cat.id} value={cat.category_name}>
                        <div className="flex items-center gap-2">
                          {cat.category_name}
                          {cat.status === 'pending' &&
                    <Badge className="text-xs bg-amber-500/20 text-amber-400">Pending</Badge>
                    }
                          {isAdmin &&
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCategoryMutation.mutate(cat.id);
                      }}
                      className="ml-1 hover:text-red-400">

                              <Trash2 className="w-3 h-3" />
                            </button>
                    }
                        </div>
                      </SelectItem>
                )}
                    <SelectItem value="+Add New" className="text-indigo-400">
                      + Add New Category
                    </SelectItem>
                  </SelectContent>
                </Select>
            }
            </div>

            <div className="w-32 space-y-2">
              <Label className="text-xs text-slate-700">Level</Label>
              <Select
              value={newSkillLevel.toString()}
              onValueChange={(v) => setNewSkillLevel(parseInt(v))}>

                <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Novice</SelectItem>
                  <SelectItem value="2">2 - Beginner</SelectItem>
                  <SelectItem value="3">3 - Intermediate</SelectItem>
                  <SelectItem value="4">4 - Advanced</SelectItem>
                  <SelectItem value="5">5 - Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button data-testid="officers-add-btn" onClick={handleAddSkill} disabled={!newSkillName}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setUseCustomSkill(!useCustomSkill)}
          className="text-xs text-slate-600 hover:text-slate-900">

            {useCustomSkill ? 'Use preset skills' : 'Add custom skill'}
          </Button>
        </div>
      }

      {/* Custom Category Input Dialog */}
      <Dialog open={showCustomCategoryInput} onOpenChange={setShowCustomCategoryInput}>
        <DialogContent data-testid="officers-skillsmanager-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>Propose New Skill Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input data-testid="officers-skillsmanager-input"
                value={customCategoryName}
                onChange={(e) => setCustomCategoryName(e.target.value)}
                placeholder="e.g., Project Management, Data Science..."
                className="bg-slate-950 border-slate-700"
                autoFocus />

            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowCustomCategoryInput(false)}
                variant="outline" className="bg-background text-slate-900 px-4 py-2 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-9 flex-1">


                Cancel
              </Button>
              <Button
                onClick={handleAddCustomCategory}
                disabled={!customCategoryName.trim()}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700">

                Add Category
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Languages Selection Dialog */}
      <Dialog open={showLanguageDialog} onOpenChange={setShowLanguageDialog}>
        <DialogContent data-testid="officers-skillsmanager-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Languages</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Choose languages and set proficiency level</Label>
              <div className="grid grid-cols-3 gap-2 max-h-[400px] overflow-y-auto p-2">
                {LANGUAGE_OPTIONS.map((lang) =>
                <div
                  key={lang}
                  onClick={() => {
                    setSelectedLanguages((prev) =>
                    prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
                    );
                  }}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedLanguages.includes(lang) ?
                  'border-indigo-500 bg-indigo-500/10' :
                  'border-slate-700 hover:border-slate-600'}`
                  }>

                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    selectedLanguages.includes(lang) ? 'border-indigo-500 bg-indigo-500' : 'border-slate-600'}`
                    }>
                        {selectedLanguages.includes(lang) &&
                      <div className="w-2 h-2 rounded-full bg-white" />
                      }
                      </div>
                      <span className="text-sm">{lang}</span>
                    </div>
                  </div>
                )}
              </div>
              {selectedLanguages.length > 0 &&
              <div className="flex flex-wrap gap-2 p-3 bg-slate-950 rounded border border-slate-800">
                  {selectedLanguages.map((lang) =>
                <Badge key={lang} className="bg-indigo-500/20 text-indigo-400">
                      {lang}
                      <button
                    onClick={() => setSelectedLanguages((prev) => prev.filter((l) => l !== lang))}
                    className="ml-1 hover:text-red-400">

                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                )}
                </div>
              }
            </div>
            <div className="space-y-2">
              <Label>Proficiency Level (applies to all selected)</Label>
              <Select
                value={newSkillLevel.toString()}
                onValueChange={(v) => setNewSkillLevel(parseInt(v))}>

                <SelectTrigger className="bg-slate-950 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Novice</SelectItem>
                  <SelectItem value="2">2 - Beginner</SelectItem>
                  <SelectItem value="3">3 - Intermediate</SelectItem>
                  <SelectItem value="4">4 - Advanced</SelectItem>
                  <SelectItem value="5">5 - Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowLanguageDialog(false);
                  setSelectedLanguages([]);
                }}
                variant="outline" className="bg-background text-slate-900 px-4 py-2 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-9 flex-1">


                Cancel
              </Button>
              <Button
                onClick={handleAddLanguages}
                disabled={selectedLanguages.length === 0}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700">

                Add {selectedLanguages.length} Language{selectedLanguages.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>);

}